import React from 'react';
import { Box, Typography, Paper, IconButton, Divider } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useWorkflow, WorkflowStep } from '../../contexts/WorkflowContext';
import ConceptCard from './ConceptCard';
import DiagramViewer from './DiagramViewer';

const LearningPanel: React.FC = () => {
  const { learningMode, toggleLearningMode, currentStep } = useWorkflow();

  if (!learningMode) return null;

  const getContentForStep = (step: WorkflowStep) => {
    switch (step) {
      case 'discovery':
        return (
          <>
            <ConceptCard
              title="Problem Decomposition"
              summary="Breaking down complex problems into smaller, manageable parts."
              details="AI agents work best when given specific, narrow tasks. By decomposing a large problem (e.g., 'Plan a trip') into smaller components (e.g., 'Search flights', 'Book hotel'), we can design more effective agents."
            />
            <ConceptCard
              title="Agent Persona"
              summary="Defining the role and behavior of your agent."
              details="A persona gives the LLM context on how to behave. Is it a helpful assistant, a strict auditor, or a creative writer? This influences the tone and style of responses."
            />
          </>
        );
      case 'design':
        return (
          <>
            <ConceptCard
              title="Capabilities & Tools"
              summary="What the agent can actually DO."
              details="LLMs are just text generators. To perform actions, they need 'Tools' - functions they can call to interact with the outside world (APIs, databases, files)."
            />
            <DiagramViewer
              title="Tool Execution Loop"
              imageUrl="/diagrams/tool-loop.png"
              description="How an agent decides to use a tool and processes the result."
            />
          </>
        );
      case 'multiagent':
        return (
          <>
            <ConceptCard
              title="Orchestration Patterns"
              summary="How multiple agents work together."
              details="Hierarchical: A manager delegates to workers. Sequential: Output of one is input to next. Joint: Agents converse to solve a problem."
            />
            <DiagramViewer
              title="Hierarchical vs Sequential"
              imageUrl="/diagrams/patterns.png"
              description="Visual comparison of agent architectures."
            />
          </>
        );
      case 'technical':
        return (
          <>
            <ConceptCard
              title="Context Window"
              summary="The amount of information the model can remember."
              details="Models have a limit on how much text they can process at once. Efficient state management is crucial to keep relevant info in context without overflowing."
            />
            <ConceptCard
              title="Temperature"
              summary="Controlling randomness in generation."
              details="Low temperature (0.0) makes the model deterministic and focused. High temperature (0.9) makes it more creative and random."
            />
          </>
        );
      case 'codegen':
        return (
          <>
            <ConceptCard
              title="System Prompt"
              summary="The initial instruction that sets the behavior."
              details="This is the most critical part of the code. It defines who the agent is, what it can do, and its constraints."
            />
          </>
        );
      case 'operations':
        return (
          <>
            <ConceptCard
              title="Inference Costs"
              summary="Understanding how you pay for AI."
              details="Costs are usually based on 'tokens' (roughly 0.75 words). You pay for both input (what you send) and output (what the model writes)."
            />
          </>
        );
      default:
        return <Typography>Select a step to see learning content.</Typography>;
    }
  };

  return (
    <Paper
      elevation={3}
      sx={{
        width: 320,
        height: 'calc(100vh - 64px)',
        position: 'fixed',
        right: 0,
        top: 64,
        zIndex: 1200,
        display: 'flex',
        flexDirection: 'column',
        borderLeft: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        p={2}
        bgcolor="primary.main"
        color="primary.contrastText"
      >
        <Typography variant="h6">Learning Mode</Typography>
        <IconButton
          onClick={toggleLearningMode}
          size="small"
          sx={{ color: 'inherit' }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <Box
        sx={{
          flexGrow: 1,
          overflowY: 'auto',
          p: 2,
          bgcolor: 'background.default',
        }}
      >
        <Typography
          variant="subtitle2"
          color="text.secondary"
          gutterBottom
          sx={{
            textTransform: 'uppercase',
            fontSize: '0.75rem',
            letterSpacing: 1,
          }}
        >
          Concepts for {currentStep}
        </Typography>
        <Divider sx={{ mb: 2 }} />

        {getContentForStep(currentStep)}
      </Box>
    </Paper>
  );
};

export default LearningPanel;
